import { useCallback, useMemo, useRef, useState } from 'react';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { getErrorMessage } from 'UI/utils';

import {
  ERROR_RESPONSE_CUSTOM_CODES,
  QUERY_PARAMS_BY_ENTITY_TYPE
} from './BulkEmailDrawerAI.constants';
import { generateBulkEmail, rateBulkEmailAiGenerated } from './BulkEmailDrawerAI.services';
import {
  areEntityIdsDifferent,
  buildEntityEndpoint,
  createBulkEmailData,
  determineEntityType,
  formatEmailContent,
  shouldEnableAction,
  shouldEnableAIDrawer
} from './BulkEmailDrawerAI.utils';

export const useEntityConfiguration = ({ jobOrders, candidates }) => {
  const entity = useMemo(
    () => determineEntityType({ jobOrders, candidates }),
    [jobOrders, candidates]
  );

  const config = useMemo(() => {
    if (entity === EntityType.Joborder) {
      return {
        inputList: jobOrders,
        queryParams: QUERY_PARAMS_BY_ENTITY_TYPE[EntityType.Joborder],
        endpoint: Endpoints.JobOrders,
        apiVersion: 1,
        pluralEntity: 'job orders'
      };
    }

    if (entity === EntityType.Candidate) {
      return {
        inputList: candidates,
        queryParams: QUERY_PARAMS_BY_ENTITY_TYPE[EntityType.Candidate],
        endpoint: Endpoints.Candidates,
        apiVersion: 2,
        pluralEntity: 'candidates'
      };
    }

    return null;
  }, [entity, jobOrders, candidates]);

  return { entity, config };
};

export const useAIGenerator = ({ bulkData, onUseThisEmail }) => {
  const { showAlert } = useShowAlert();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const generatedAiData = useRef(null);

  const isAIRecruitingFeatureActive = checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailAIRecruiting);
  const shouldShowAIButton =
    checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailAI) &&
    userHasPermission(Permissions.BulkEmail.AIBuilder);

  const { config, entity } = useEntityConfiguration({
    jobOrders: bulkData.jobOrderRecruitment,
    candidates: bulkData.marketCandidates
  });

  const { inputList, queryParams, endpoint, pluralEntity, apiVersion } = config || {};

  const entityIds = inputList?.map(item => item.id);
  if (!isAIDrawerOpen && generatedAiData.current) {
    generatedAiData.current = null;
  }

  const fullEndpoint = useMemo(() => {
    if (!shouldEnableAIDrawer({ isAIDrawerOpen, entityIds })) return null;
    return buildEntityEndpoint({ endpoint, queryParams, entityIds });
  }, [entityIds, queryParams, endpoint, isAIDrawerOpen]);

  const entityResult = useFetchWithStatusV2({
    endpoint: fullEndpoint,
    apiVersion
  });

  const entityData = entityResult?.state?.results?.data;

  const handleSubmit = useCallback(() => {
    if (!generatedAiData.current) return;
    onUseThisEmail({
      emailBody: generatedAiData.current.variant.body,
      promptId: generatedAiData.current.id,
      subject: generatedAiData.current.variant.subject
    });
    setIsAIDrawerOpen(false);
  }, [onUseThisEmail]);

  const handleOpenAIDrawer = useCallback(() => {
    setIsAIDrawerOpen(true);
  }, []);

  const handleCloseAIDrawer = useCallback(() => {
    setIsAIDrawerOpen(false);
  }, []);

  const handleClickRateButton = useCallback(
    async ({ id, rating }) => {
      if (!id) return false;

      const showRatingErrorAlert = error => {
        showAlert({
          title: strings.bulkEmails.emailGenerator.ratingErrorTitle,
          body: getErrorMessage(
            error || null,
            strings.bulkEmails.emailGenerator.ratingErrorMessage
          ),
          severity: 'error'
        });
      };

      try {
        const result = await rateBulkEmailAiGenerated({ id, score: rating });
        if (
          [HTTPStatusCodes.Ok, HTTPStatusCodes.Created, HTTPStatusCodes.NoContent].includes(
            result.status
          )
        ) {
          return true;
        }
        showRatingErrorAlert(null);
        return false;
      } catch (error) {
        showRatingErrorAlert(error);
        return false;
      }
    },
    [showAlert]
  );

  const handleGenerateWithAi = useCallback(
    async formData => {
      const finalData = createBulkEmailData({
        ...bulkData,
        ...formData,
        candidates: entity === EntityType.Candidate ? entityData : null,
        jobOrders: entity === EntityType.Joborder ? entityData : null
      });

      try {
        const { data } = await generateBulkEmail({
          ...finalData,
          regenerateId: generatedAiData.current?.id
        });
        generatedAiData.current = data;
        return { id: data?.id, generatedContent: formatEmailContent(data.variant) };
      } catch (error) {
        if (error.response?.data?.code !== ERROR_RESPONSE_CUSTOM_CODES.NotEnoughData) {
          showAlert({
            body: getErrorMessage(error, strings.bulkEmails.emailGenerator.generalError.message),
            title: strings.bulkEmails.emailGenerator.generalError.title,
            severity: 'error'
          });
          return null;
        }

        throw new Error(getErrorMessage(error));
      }
    },
    [bulkData, entity, showAlert, entityData]
  );

  const finalHandleGenerateWithAi = useMemo(() => {
    if (
      !entity ||
      !entityData ||
      areEntityIdsDifferent({ currentEntities: inputList, newEntities: entityData })
    ) {
      return null;
    }
    return handleGenerateWithAi;
  }, [entity, entityData, handleGenerateWithAi, inputList]);

  const buttonDefinitions = [
    {
      props: {
        text: strings.bulkEmails.emailGenerator.submitButtonName,
        onClick: handleSubmit
      }
    }
  ];

  const shouldEnableAIAction = useMemo(
    () => shouldEnableAction(bulkData, { isAIRecruitingFeatureActive }),
    [bulkData, isAIRecruitingFeatureActive]
  );

  return {
    buttonDefinitions,
    entity,
    entityResult,
    handleClickRateButton,
    handleCloseAIDrawer,
    handleGenerateWithAi: finalHandleGenerateWithAi,
    handleOpenAIDrawer,
    isAIDrawerOpen,
    pluralEntity,
    shouldEnableAIAction,
    shouldShowAIButton
  };
};
